import { lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CloudUploadOutlined, DatabaseOutlined, MenuOutlined } from "@ant-design/icons";
import { App, Layout, Menu, Popover, Progress, Result, Tag, theme, Drawer } from "antd";
import { matchPath, Outlet, useLocation, useMatches, useNavigate } from "react-router-dom";
import { useMediaQuery, useOnClickOutside } from "usehooks-ts";
import Login from "./Login";
import UploadProvider from "/src/components/UploadProvider";
import { AuthContext, SupportNudgeContext } from "/src/constants/contexts";
import { useTheme } from "/src/contexts/ThemeContext";
import useIsDesktop from "/src/hooks/useIsDesktop";
import { clsx } from "clsx";
import useUser from "../../hooks/useUser";
import useUploadQuota from "../../hooks/useUploadQuota";
import { isActiveCustomer } from "../../hooks/useIsActiveCustomer";
import { DISABLE_UPLOAD, IS_WHITE_LABEL } from "../../constants";
import useOrg from "../../hooks/apis/useOrg";
import useEarlyPaymentStatus from "../../hooks/apis/useEarlyPaymentStatus";
import usePersistedState from "../../hooks/usePersistedState";
import useHttpClient from "../../hooks/useHttpClient";
import useResponsiveModal from "../../hooks/useResponsiveModal";
import useOrgTerminology from "../../hooks/useOrgTerminology";
import { trackPendingLogin } from "../../utils/gtm";
import { playNudgeSound } from "../../utils/sound";
import {
  PoweroffOutlined,
  UserOutlined,
  MessageOutlined,
  SunFilled,
  MoonFilled,
  PoundOutlined,
  BankOutlined,
  ShopOutlined,
  ShoppingOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import Loading from "../../components/loading";
import MobileTopNav, {
  MobileNavProvider,
  useMobileNavRoute,
} from "../../components/MobileTopNav";

const WhiteLabelMenuIcon = lazy(() => import("../../components/WhiteLabelMenuIcons"));

function menuIcon(isWhiteLabel, name, fallback) {
  return isWhiteLabel ? (
    <Suspense fallback={fallback}>
      <WhiteLabelMenuIcon name={name} />
    </Suspense>
  ) : fallback;
}

const { Sider, Content } = Layout;
function useMenus() {
  const navigate = useNavigate();
  const { data: org } = useOrg();
  const { data: userData } = useUser();
  const matchedRoutes = useMatches();
  const selectedKeyRef = useRef();
  const { message } = App.useApp();
  const { confirm, contextHolder: supportConfirmHolder } = useResponsiveModal();
  const { handleLogout, user } = useContext(AuthContext);
  const httpClient = useHttpClient();
  const [supportToken, setSupportToken] = useState("");
  const uploadingVideo = UploadProvider.useUploadingVideo();
  const uploadingProgress = uploadingVideo?.progress;
  const supportBotName = org?.config?.telegramSupportUsername;
  const terminology = useOrgTerminology();
  const isWhiteLabel = org?.config?.whiteLabel ?? IS_WHITE_LABEL;

  const uploadFeatureOff = isWhiteLabel || DISABLE_UPLOAD;
  const { quota } = useUploadQuota({ enabled: Boolean(user) && !uploadFeatureOff });
  const uploadDisabled = Boolean(quota?.disabled);

  const loadSupportToken = useCallback(async () => {
    const response = await httpClient.post("/auth/ephemeral-token/");
    return response?.data?.data?.token || "";
  }, [httpClient]);

  useEffect(() => {
    if (!user || isWhiteLabel || !supportBotName || userData?.telegramId) {
      setSupportToken("");
      return;
    }

    let ignore = false;
    loadSupportToken()
      .then((token) => {
        if (!ignore) setSupportToken(token);
      })
      .catch(() => {
        if (!ignore) setSupportToken("");
      });

    return () => {
      ignore = true;
    };
  }, [isWhiteLabel, loadSupportToken, supportBotName, user, userData?.telegramId]);

  const openSupport = useCallback(async () => {
    if (!supportBotName) {
      message.error("پشتیبانی در حال حاضر در دسترس نیست");
      return;
    }

    confirm({
      icon: <MessageOutlined />,
      title: "باز کردن تلگرام",
      content: "برای ارتباط با پشتیبانی به تلگرام منتقل می‌شوید. ادامه می‌دهید؟",
      okText: "بله، ادامه می‌دهم",
      cancelText: "خیر",
      onOk: async () => {
        let href = `https://t.me/${supportBotName}`;
        if (!userData?.telegramId) {
          const token =
            supportToken ||
            (await loadSupportToken()
              .then((nextToken) => {
                setSupportToken(nextToken);
                return nextToken;
              })
              .catch(() => ""));
          if (!token) {
            message.error("امکان دریافت لینک پشتیبانی وجود ندارد");
            return;
          }
          href = `${href}?start=${token}`;
        }

        window.open(href, "_blank", "noopener,noreferrer");
      },
    });
  }, [confirm, loadSupportToken, message, supportBotName, supportToken, userData?.telegramId]);

  const rawMenus = useMemo(
    () =>
      [
        {
          path: "/finance",
          icon: menuIcon(isWhiteLabel, "finance", <PoundOutlined />),
          label: terminology.financeMenuLabel,
        },
        ...(isWhiteLabel
          ? [{ path: "/channels", icon: menuIcon(true, "channels", <YoutubeOutlined />), label: terminology.channelsMenuLabel }]
          : []),
        ...(org?.config?.marketplaceEnabled
          ? [
              {
                path: "/marketplace",
                icon: menuIcon(isWhiteLabel, "marketplace", <ShopOutlined />),
                label: (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    {terminology.marketplaceMenuLabel}
                    <Tag color="red" style={{ fontSize: 10, padding: "0 4px", lineHeight: "18px" }}>
                      جدید
                    </Tag> 
                  </span>
                ),
              },
              { path: "/orders", icon: menuIcon(isWhiteLabel, "orders", <ShoppingOutlined />), label: terminology.ordersMenuLabel },
            ]
          : []),
        ...(!isWhiteLabel && !DISABLE_UPLOAD && !uploadDisabled
          ? [
              {
                path: "/videos",
                icon: uploadingProgress ? (
                  <Progress
                    showInfo={false}
                    type="circle"
                    percent={uploadingProgress}
                    size={16}
                    strokeColor="red"
                  />
                ) : (
                  <CloudUploadOutlined />
                ),
                label: "آپلود ویدیو",
              },
            ]
          : []),
        {
          path: "/settings",
          icon: menuIcon(isWhiteLabel, "account", <UserOutlined />),
          label: isWhiteLabel ? terminology.accountMenuLabel : "پروفایل",
        },
        ...(!isWhiteLabel && supportBotName
          ? [
              {
                path: "/support",
                icon: <MessageOutlined />,
                label: "پشتیبانی",
                onClick: openSupport,
              },
            ]
          : []),
        {
          path: "/logout",
          icon: menuIcon(isWhiteLabel, "logout", <PoweroffOutlined />),
          label: terminology.logoutMenuLabel,
          onClick: handleLogout,
        },
      ].map(({ path, onClick, ...m }) => ({
        ...m,
        onClick: ({ domEvent: e }) => {
          e.preventDefault();
          if (onClick) {
            onClick(e);
            return;
          }
          navigate(path);
        },
        key: path,
      })),
    [handleLogout, isWhiteLabel, openSupport, org, supportBotName, terminology, uploadDisabled, uploadingProgress, navigate]
  );

  // Sidebar: marketplace with orders as submenu; mobile keeps flat list
  const menus = useMemo(() => rawMenus, [rawMenus]);
  const sidebarItems = useMemo(() => {
    const marketplaceItem = rawMenus.find((m) => m.key === "/marketplace");
    const ordersItem = rawMenus.find((m) => m.key === "/orders");
    if (!marketplaceItem || !ordersItem) return rawMenus;
    const rest = rawMenus.filter((m) => m.key !== "/marketplace" && m.key !== "/orders");
    const incomeIdx = rest.findIndex((m) => m.key === "/finance");
    return [
      ...rest.slice(0, incomeIdx + 1),
      {
        key: "/marketplace",
        icon: menuIcon(isWhiteLabel, "marketplace", <ShopOutlined />),
        label: (
          <span
            data-marketplace-parent
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            {terminology.marketplaceMenuLabel}
            <Tag color="red" style={{ fontSize: 10, padding: "0 4px", lineHeight: "18px" }}>
              جدید
            </Tag>
          </span>
        ),
        children: [
          { ...marketplaceItem, label: terminology.productsMenuLabel },
          { ...ordersItem, label: terminology.ordersMenuLabel },
        ],
      },
      ...rest.slice(incomeIdx + 1),
    ];
  }, [isWhiteLabel, rawMenus, terminology]);

  const selectedKey = menus
    .map(({ key }) => key)
    .find((p) => matchedRoutes.some(({ pathname }) => pathname === p));
  selectedKeyRef.current = selectedKey || selectedKeyRef.current;

  const showMarketplace = Boolean(org?.config?.marketplaceEnabled);

  return {
    menus,
    sidebarItems,
    selectedKey: selectedKeyRef.current,
    showMarketplace,
    supportConfirmHolder,
    terminology,
  };
}

function Panel() {
  const sidebarRef = useRef();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isLargeDesktop = useMediaQuery("(min-width: 1280px)");
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [collapsed, setCollapsed] = usePersistedState(
    () => window.innerWidth < 768,
    "panel/collapsed"
  );
  const { user } = useContext(AuthContext);
  const { data: org } = useOrg();
  const isWhiteLabel = org?.config?.whiteLabel ?? IS_WHITE_LABEL;
  const mobileNavRoute = useMobileNavRoute();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { data: myData, isLoading: myDataLoading, error: myDataError } = useUser(!!user);
  const { data: earlyPaymentStatus, error: earlyPaymentStatusError } = useEarlyPaymentStatus(!!user);
  useOnClickOutside(sidebarRef, () => {
    if (isMobile && bottomSheetOpen) setBottomSheetOpen(false);
  });

  // Authenticated (first mount after the post-login reload): navigate to the
  // captured destination — this half never depends on org, so it always runs
  // immediately. Unauthenticated: save the target URL — for a direct deep
  // link this is immediate too, but for the bare-root default destination we
  // must wait for org to load (useOrg() is async and is undefined on the
  // first render), otherwise `!org?.config?.marketplaceEnabled` is trivially
  // true and we wrongly default to /finance even when marketplace is
  // enabled. Depending on [user, org] (rather than running once on mount)
  // lets the effect retry once org resolves instead of locking in a guess.
  useEffect(() => {
    if (user) {
      const redirect = sessionStorage.getItem('post_login_redirect');
      if (redirect) {
        sessionStorage.removeItem('post_login_redirect');
        navigate(redirect, { replace: true });
      }
      return;
    }
    if (sessionStorage.getItem('post_login_redirect')) return;
    if (location.pathname !== '/') {
      sessionStorage.setItem('post_login_redirect', location.pathname + location.search);
      return;
    }
    if (!IS_WHITE_LABEL && org === undefined) return;
    const defaultPath = IS_WHITE_LABEL || !org?.config?.marketplaceEnabled ? '/finance' : '/marketplace';
    sessionStorage.setItem('post_login_redirect', defaultPath);
  }, [user, org]);

  useEffect(() => {
    // Wait for the user record, then for early-payment status to either resolve or
    // fail — don't couple the login event to the EP endpoint succeeding, or a single
    // EP error would silently drop login tracking for the whole session.
    if (!myData || (!earlyPaymentStatus && !earlyPaymentStatusError)) return;
    trackPendingLogin({
      is_youber_creator: isActiveCustomer(myData.channels),
      monthly_income_gbp: myData.averageIncome ?? 0,
      is_eligible_for_early_income: earlyPaymentStatus?.available ?? false,
    });
  }, [myData, earlyPaymentStatus, earlyPaymentStatusError]);

  const {
    token: {
      colorBgContainer,
      colorPrimary,
      colorBgLayout,
      colorText,
      colorTextSecondary,
      colorBorderSecondary,
      colorFillTertiary,
      boxShadowSecondary,
    },
  } = theme.useToken();
  const { menus, sidebarItems, selectedKey, showMarketplace, supportConfirmHolder, terminology } = useMenus();
  const [sidebarOpenKeys, setSidebarOpenKeys] = useState([]);
  const prevSelectedKeyRef = useRef(selectedKey);

  // useEffect(() => {
  //   if (!myData) return;
  //   const show = myData.channels.some((ch) => ch.state === "ACTIVE_CUSTOMER");
  //   console.log(show);
  //   if (location.pathname === "/" && show) {
  //     Router.navigate("/marketplace");
  //   }
  // }, [location.pathname, myData]);

  useEffect(() => {
    const prev = prevSelectedKeyRef.current;
    prevSelectedKeyRef.current = selectedKey;
    if ((selectedKey === "/marketplace" || selectedKey === "/orders") && prev !== selectedKey) {
      setSidebarOpenKeys((k) => [...new Set([...k, "/marketplace"])]);
    }
  }, [selectedKey]);

  const { isDark, toggleTheme, orgTheme } = useTheme();

  const effectiveCollapsed = isLargeDesktop ? false : !!collapsed;
  const collapsedWidth = !isDesktop ? 40 : 80;

  // Bottom nav: when marketplace is enabled, show marketplace instead of settings
  const bottomNavItems = useMemo(() => {
    const hasMarketplace = menus.some((m) => m.key === "/marketplace");
    return menus.filter(
      (menu) =>
        menu.key !== "/logout" &&
        menu.key !== "/orders" &&
        (hasMarketplace ? menu.key !== "/settings" : menu.key !== "/marketplace")
    );
  }, [menus]);

  // Menu button for bottom nav
  const menuButton = {
    key: "menu",
    icon: menuIcon(isWhiteLabel, "menu", <MenuOutlined />),
    label: terminology.menuButtonLabel,
  };

  // Support nudge: on a product page, after 15s without purchase engagement,
  // point the user at the support icon in the bottom nav. Once per session;
  // the product page suppresses it via SupportNudgeContext when the purchase
  // modal opens.
  const [supportNudgeOpen, setSupportNudgeOpen] = useState(false);
  const supportNudgeSuppressedRef = useRef(false);
  const suppressSupportNudge = useCallback(() => {
    supportNudgeSuppressedRef.current = true;
    setSupportNudgeOpen(false);
  }, []);
  const supportNudgeValue = useMemo(() => ({ suppressSupportNudge }), [suppressSupportNudge]);
  const onProductPage = Boolean(matchPath("/marketplace/products/:id", location.pathname));
  const hasSupportNav = bottomNavItems.some((menu) => menu.key === "/support");
  useEffect(() => {
    if (!isMobile || !onProductPage || !hasSupportNav) return undefined;
    if (sessionStorage.getItem("support_nudge_shown")) return undefined;
    supportNudgeSuppressedRef.current = false;
    const timer = setTimeout(() => {
      if (supportNudgeSuppressedRef.current) return;
      setSupportNudgeOpen(true);
      playNudgeSound();
      sessionStorage.setItem("support_nudge_shown", "1");
    }, 15000);
    return () => {
      clearTimeout(timer);
      setSupportNudgeOpen(false);
    };
  }, [isMobile, onProductPage, hasSupportNav]);
  useEffect(() => {
    if (!supportNudgeOpen) return undefined;
    const timer = setTimeout(() => setSupportNudgeOpen(false), 8000);
    return () => clearTimeout(timer);
  }, [supportNudgeOpen]);

  if (!user) {
    return <Login />;
  }

  function renderContent() {
    if (myDataLoading) {
      return <Loading size="fill" text="در حال بارگذاری..." />;
    }
    if (myDataError) {
      return (
        <Result
          status="error"
          icon={<DatabaseOutlined />}
          title="خطایی رخ داده است"
          subTitle="لطفا کمی بعد دوباره تلاش کنید"
        />
      );
    }
    return (
      <Suspense fallback={<Loading size="fill" text="در حال بارگذاری..." />}>
        <Outlet />
      </Suspense>
    );
  }

  return (
    <UploadProvider>
      <MobileNavProvider>
      <SupportNudgeContext.Provider value={supportNudgeValue}>
      {supportConfirmHolder}
      <Layout style={{
        minHeight: "100vh",
        width: "auto",
        backgroundColor: colorBgLayout,
      }}>
        {/* Fixed top navbar on mobile for non-root routes (title + back) */}
        {isMobile && <MobileTopNav />}
        {/* Sidebar - hidden on mobile */}
        {!isMobile && (
          <Sider
            ref={sidebarRef}
            collapsible
            trigger={null}
            collapsedWidth={collapsedWidth}
            collapsed={effectiveCollapsed}
            onCollapse={setCollapsed}
            theme={isDark ? "dark" : "light"}
            style={{
              zIndex: 1001,
              overflow: "auto",
              height: "100vh",
              position: "fixed",
              right: 0,
              top: 0,
              bottom: 0,
              backgroundColor: colorBgContainer,
              boxShadow: boxShadowSecondary,
              padding: 10,
            }}
            onClick={(e) => {
              if (isLargeDesktop) return;
              if (e.defaultPrevented) {
                return;
              }
              if (e.target.closest?.("li")?.querySelector?.("[data-marketplace-parent]")) {
                return;
              }
              setCollapsed((c) => !c);
            }}
          >
            <div
              style={{
                width: "100%",
                height: 80,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="my-4"
            >
              {collapsed && (orgTheme?.logoSmall || orgTheme?.logo) && (
                <img
                  src={isDark ? orgTheme?.logoSmallDark || orgTheme?.logoSmall || orgTheme?.logoDark || orgTheme?.logo : orgTheme?.logoSmall || orgTheme?.logo}
                  className="max-h-full w-[60%] object-scale-down"

                  alt={`${orgTheme?.title} logo`}
                />
              )}
              {!collapsed && (orgTheme?.logo || orgTheme?.logoDark) && (
                <img
                  src={isDark ? orgTheme?.logoDark || orgTheme?.logo : orgTheme?.logo}
                  className="max-h-full max-w-full p-4 object-scale-down"
                  alt={`${orgTheme?.title} logo`}
                />
              )}
              {!orgTheme?.logo && !orgTheme?.logoDark && <span>{orgTheme?.title}</span>}
            </div>

            <Menu
              className="sidebar-menu"
              mode="inline"
              selectedKeys={[selectedKey]}
              openKeys={sidebarOpenKeys}
              onOpenChange={setSidebarOpenKeys}
              items={sidebarItems}
              style={{ border: "none" }}
              theme={isDark ? "dark" : "light"}
            />

            <div
              style={{
                position: "absolute",
                bottom: 10,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Menu
                style={{
                  border: "none",
                  backgroundColor: "transparent",
                }}
                mode="inline"
                items={[
                  {
                    key: "theme",
                    icon: isDark ? <SunFilled /> : <MoonFilled />,
                    label: isDark ? "روشن" : "تاریک",
                    onClick: ({ domEvent: e }) => {
                      e.stopPropagation();
                      toggleTheme();
                    },
                  },
                ]}
              />
            </div>
          </Sider>
        )}

        {/* Bottom Sheet Drawer for Mobile Menu */}
        {isMobile && (
          <Drawer
            placement="bottom"
            onClose={() => setBottomSheetOpen(false)}
            open={bottomSheetOpen}
            height="auto"
            styles={{
              body: { padding: 0 },
              wrapper: { zIndex: 1000 },
            }}
            closeIcon={null}
            style={{
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
            }}
            rootStyle={{
              zIndex: 999,
            }}
          >
            <div
              style={{
                padding: "12px 0 72px 0",
              }}
            >
              {/* Menu Items */}
              <div
                style={{
                  padding: "0 16px",
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "8px",
                }}
              >
                {menus.map((menu) => {
                  const isSelected = menu.key === selectedKey;
                  return (
                    <div
                      key={menu.key}
                      onClick={(e) => {
                        e.preventDefault();
                        setBottomSheetOpen(false);
                        if (menu.onClick) {
                          menu.onClick({ domEvent: e });
                        }
                      }}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "12px 8px",
                        cursor: "pointer",
                        backgroundColor: isSelected ? colorFillTertiary : "transparent",
                        borderRadius: 10,
                        color: isSelected ? colorPrimary : colorText,
                        transition: "all 0.2s",
                      }}
                    >
                      <div style={{ fontSize: "22px", marginBottom: 6 }}>{menu.icon}</div>
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: isSelected ? 600 : 400,
                          textAlign: "center",
                        }}
                      >
                        {menu.label}
                      </div>
                    </div>
                  );
                })}

                {/* Theme Toggle */}
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleTheme();
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "12px 8px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    borderRadius: 10,
                    color: colorText,
                    transition: "all 0.2s",
                  }}
                >
                  <div style={{ fontSize: "22px", marginBottom: 6 }}>
                    {isDark ? <SunFilled /> : <MoonFilled />}
                  </div>
                  <div style={{ fontSize: "12px", textAlign: "center" }}>
                    {isDark ? "روشن" : "تاریک"}
                  </div>
                </div>
              </div>
            </div>
          </Drawer>
        )}

        <Layout
          style={{
            transition: "margin-right 0.2s ease",
            marginRight: isMobile ? 0 : effectiveCollapsed ? collapsedWidth : 200,
            // "clip" instead of "hidden": hidden creates a scroll container,
            // which breaks position:sticky descendants (marketplace tag bar).
            overflow: "clip",
            maxWidth: 1450,
            background:'transparent'
          }}
        >
          <Content
            className={clsx("m-2 px-2 py-2 md:px-10 md:py-5 ", {
              "pb-20": isMobile,
              "pt-16": isMobile && Boolean(mobileNavRoute),
            })}
          >
            {renderContent()}
          </Content>
        </Layout>

        {/* Bottom Navigation for Mobile */}
        {isMobile && (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 900,
              backgroundColor: colorBgContainer,
              boxShadow: boxShadowSecondary,
              borderTop: `1px solid ${colorBorderSecondary}`,
              paddingTop: 4,
              paddingBottom: 2,
              borderStartStartRadius: 32,
              borderStartEndRadius: 32,
            }}
          >
            {/* Pages can portal content into the top of the nav (e.g. marketplace search) */}
            <div id="mobile-bottom-nav-slot" className="pt-3" />
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                width: "100%",
              }}
            >
              {/* Menu Button */}
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setBottomSheetOpen((prev) => !prev);
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  padding: "2px 8px",
                  cursor: "pointer",
                  color: bottomSheetOpen ? colorPrimary : colorTextSecondary,
                  transition: "color 0.2s",
                }}
              >
                <div style={{ fontSize: "18px", marginBottom: -5 }}>{menuButton.icon}</div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: bottomSheetOpen ? 600 : 400,
                  }}
                >
                  {menuButton.label}
                </div>
              </div>

              {/* Other navigation items */}
              {bottomNavItems.map((menu) => {
                const isSelected = menu.key === selectedKey;
                const isMarketplace = menu.key === "/marketplace";
                const bottomNavIcon = isMarketplace ? (
                  <span style={{ position: "relative", display: "inline-block" }}>
                    {menu.icon}
                    <Tag
                      color="red"
                      style={{
                        position: "absolute",
                        top: -6,
                        left: -22,
                        fontSize: 10,
                        padding: "0 4px",
                        lineHeight: "18px",
                        margin: 0,
                      }}
                    >
                      جدید
                    </Tag>
                  </span>
                ) : (
                  menu.icon
                );
                const bottomNavLabel = isMarketplace ? terminology.marketplaceMenuLabel : menu.label;
                const navItem = (
                  <div
                    key={menu.key}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (supportNudgeOpen) {
                        setSupportNudgeOpen(false);
                      }
                      // Close bottom sheet if open
                      if (bottomSheetOpen) {
                        setBottomSheetOpen(false);
                      }
                      // Execute menu action
                      if (menu.onClick) {
                        menu.onClick({ domEvent: e });
                      }
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                      padding: "2px 8px",
                      cursor: "pointer",
                      color: isSelected ? colorPrimary : colorTextSecondary,
                      transition: "color 0.2s",
                    }}
                  >
                    <div style={{ fontSize: "18px", marginBottom: -5 }}>{bottomNavIcon}</div>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: isSelected ? 600 : 400,
                      }}
                    >
                      {bottomNavLabel}
                    </div>
                  </div>
                );
                if (menu.key === "/support") {
                  return (
                    <Popover
                      key={menu.key}
                      open={supportNudgeOpen && !bottomSheetOpen}
                      placement="top"
                      content={
                        <div
                          style={{ cursor: "pointer", fontWeight: 500 }}
                          onClick={(e) => {
                            setSupportNudgeOpen(false);
                            menu.onClick?.({ domEvent: e });
                          }}
                        >
                          اگر به کمک نیاز دارید، ما اینجاییم 👋
                        </div>
                      }
                    >
                      {navItem}
                    </Popover>
                  );
                }
                return navItem;
              })}
            </div>
          </div>
        )}
      </Layout>
      </SupportNudgeContext.Provider>
      </MobileNavProvider>
    </UploadProvider>
  );
}

export default Panel;
